import { useCallback, useContext, useState } from "react";
import { useBiometrics } from "../../../../hooks/useBiometrics";
import { ParticipantContextProps } from "../../../../models/contexts/participant-context.model";
import { ParticipantContext } from "../../../../contexts/Participant.context";
import UiFieldMessage from "../../../../components/field-message/FieldMessage.component";
import { FieldMessageSeverity } from "../../../../models/components/field-message.model";
import BiometricsChartsChart from "./BiometricsChartsChart";
import BiometricsChartsLinks from "./BiometricsChartsLinks";
import BiometricsChartsHistograms from "./BiometricsChartsHistograms";
import {
	CHART_DATA,
	CHART_FILTER,
	CHART_TYPE,
} from "../../../../models/pages/biometrics/biometrics-charts.model";
import BiometricsChartsFilters from "./BiometricsChartsFilters";
import { BiometricsTrends } from "./BiometricsTrends";
import { BiometricType } from "../../../../models/entities/biometrics.model";
import BiometricsTrendsFilters from "./BiometricsTrendsFilters";
import { TREND_FILTER_TYPE } from "../../../../models/pages/biometrics/biometrics-trends-filters";

const chartTypeToBiometricType = (chartType: CHART_TYPE): BiometricType => {
	switch (chartType) {
		case CHART_TYPE.GLUCOSE:
			return BiometricType.glucose;
		case CHART_TYPE.BLOOD_PRESSURE:
			return BiometricType.blood_pressure;
		case CHART_TYPE.WEIGHT:
			return BiometricType.weight;
	}
};

const BiometricsCharts = () => {
	const participantContext = useContext<ParticipantContextProps>(ParticipantContext);
	const biometrics = useBiometrics({
		participantId: participantContext.participant!.id,
	});

	const [chartFilters, setChartFilters] = useState<{ type: CHART_TYPE; range: CHART_FILTER }>({
		type: CHART_TYPE.GLUCOSE,
		range: CHART_FILTER.MONTH,
	});

	const [trendFilters, setTrendFilters] = useState<{ type: TREND_FILTER_TYPE }>({
		type: TREND_FILTER_TYPE.GLUCOSE,
	});

	const [chartDatasets, setChartDatasets] = useState<CHART_DATA | undefined>();

	// Calculate first biometric value
	const getFirstBiometricValue = (type: CHART_TYPE) => {
		const biometricsArray = biometrics.data?.get(chartTypeToBiometricType(type));
		let nonDeletedBiometrics = biometricsArray?.filter((biometric) => !biometric.deleted);

		// For glucose, also filter out control meal states
		if (type === CHART_TYPE.GLUCOSE) {
			nonDeletedBiometrics = nonDeletedBiometrics?.filter(
				(biometric) => biometric.mealState !== "CONTROL"
			);
		}

		if (!nonDeletedBiometrics || nonDeletedBiometrics.length === 0) {
			return "";
		}
		const oldestBiometric = nonDeletedBiometrics[nonDeletedBiometrics.length - 1];
		return oldestBiometric?.vitalTime || "";
	};

	// Add a custom handler that preserves the current type if the new one is invalid
	const handleFiltersChange = (newFilters: { type: CHART_TYPE; range: CHART_FILTER }) => {
		setChartFilters((prevFilters) => ({
			...newFilters,
			// Keep the current type if the new type is null/undefined
			type: newFilters.type || prevFilters.type,
		}));
	};

	const handleTrendsFiltersChange = (newFilters: { type: TREND_FILTER_TYPE }) => {
		setTrendFilters((prevFilters) => ({
			...newFilters,
			// Keep the current type if the new type is null/undefined
			type: newFilters.type || prevFilters.type,
		}));
	};

	const handleDatasetsChange = useCallback((data: CHART_DATA) => {
		setChartDatasets(data);
	}, []);

	return (
		<div className="biometrics-charts">
			{biometrics.isLoading || biometrics.isError ? (
				<UiFieldMessage
					severity={
						biometrics.isLoading
							? FieldMessageSeverity.LOADING
							: FieldMessageSeverity.DANGER
					}
					label={
						biometrics.isLoading
							? "PARTICIPANT.BIOMETRICS.MESSAGES.LOADING"
							: "PARTICIPANT.BIOMETRICS.MESSAGES.ERROR"
					}
				/>
			) : (
				<>
					<BiometricsChartsFilters
						filters={chartFilters}
						onFiltersChange={handleFiltersChange}
					/>
					<BiometricsChartsChart
						biometrics={biometrics.data ?? new Map()}
						filters={chartFilters}
						onDatasetsChange={handleDatasetsChange}
						firstBiometric={getFirstBiometricValue(chartFilters.type)}
					/>

					{chartFilters.type === "GLUCOSE" && (
						<BiometricsTrendsFilters
							filters={trendFilters}
							onFiltersChange={handleTrendsFiltersChange}
						/>
					)}

					{
						<BiometricsTrends
							participantId={participantContext.participant!.id}
							type={
								chartFilters.type === "GLUCOSE"
									? trendFilters.type
									: chartFilters.type
							}
							firstBiometric={getFirstBiometricValue(chartFilters.type)}
						/>
					}

					{chartFilters.type === CHART_TYPE.GLUCOSE && (
						<>
							<BiometricsChartsHistograms
								participantId={participantContext.participant!.id}
								range={chartFilters.range}
								datasets={chartDatasets}
							/>

							<BiometricsChartsLinks
								kannactId={participantContext.participant!.kannactId}
							/>
						</>
					)}
				</>
			)}
		</div>
	);
};

export default BiometricsCharts;
